/* ChicChac — reviews-app integration (mount points only)

   The store has NO reviews app installed (verified 2026-08-03: the only app
   embed is Razorpay Magic Checkout, no review metafields on any product, no
   widget on any PDP). This file ships ahead of the install so that the day the
   founder adds one, the widgets land where the design wants them without
   another theme push:

     PDP    — star badge between the <h1> and the price; the full review
              widget after the description block (.cc-desc, tagged by
              cc-product.js).
     Cards  — a rating line between the title and the price, inside
              .product-card__content. Covers Horizon's <product-card> and the
              ChicChac-authored .cc-lcard cards (cc-loved.liquid), which carry
              .product-card__content on the root element itself.

   Setup runbook: docs/launch/reviews-setup.md

   >>> INERT UNTIL AN APP EXISTS <<<
   Nothing is created, fetched, moved or logged until a vendor is positively
   detected. On today's store the whole file is a bounded poll that finds
   nothing and stops. That is deliberate — this lands on the live storefront
   while no reviews app is installed.

   Judge.me is the target. Its loader renders a widget from a data-id alone, so
   for Judge.me we can BUILD mounts as well as relocate them. Loox and Stamped
   read the rating out of a Liquid metafield that storefront JS cannot see
   (Loox needs data-rating/data-raters, Stamped needs its pre-rendered badge),
   so for those two we only RELOCATE the nodes their own installer put on the
   page. Relocation is the bulk of the value either way, and cost us one
   selector each. */
(function () {
  'use strict';

  var CARD_SEL = 'product-card, [data-cc-card]';
  var WAIT_TICK = 400;
  var WAIT_TRIES = 20; /* ~8s. An app embed that has not booted by then is not installed. */

  function has(sel) { return !!document.querySelector(sel); }

  /* ---------- vendors -------------------------------------------------
     `widget` / `badge` are the vendor's own mount classes — we look for them
     first and move the real node, because a relocated node keeps working no
     matter how the vendor rendered it (metafield or JS). `make*` is the
     fallback for when the vendor's auto-installer could not patch this custom
     theme and left nothing behind. */
  var VENDORS = [
    {
      key: 'judgeme',
      detect: function () {
        return !!(window.jdgm || window.jdgmSettings) ||
          has('script[src*="judge.me"], .jdgm-widget, .jdgm-preview-badge');
      },
      widget: '.jdgm-review-widget, #judgeme_product_reviews',
      badge: '.jdgm-preview-badge',
      makeWidget: function (id) {
        var d = document.createElement('div');
        d.id = 'judgeme_product_reviews';
        d.className = 'jdgm-widget jdgm-review-widget';
        d.setAttribute('data-id', id);
        return d;
      },
      makeBadge: function (id, template) {
        var d = document.createElement('div');
        d.className = 'jdgm-widget jdgm-preview-badge';
        d.setAttribute('data-id', id);
        /* Judge.me keys its per-surface styling off data-template. */
        d.setAttribute('data-template', template);
        return d;
      },
      paint: function () {
        var j = window.jdgm;
        if (!j) return;
        /* Both guarded by typeof: the loader's public surface is unversioned
           and these have come and gone between widget generations. */
        if (typeof j.batchRenderBadges === 'function') j.batchRenderBadges();
        if (typeof j.batchRenderReviewWidgets === 'function') j.batchRenderReviewWidgets();
      }
    },
    {
      key: 'loox',
      detect: function () {
        return !!window.loox || has('script[src*="loox.io"], .loox-rating, #looxReviews');
      },
      widget: '#looxReviews, .loox-reviews-default',
      badge: '.loox-rating',
      makeWidget: null,
      makeBadge: null,
      paint: function () {
        if (window.loox && typeof window.loox.reload === 'function') window.loox.reload();
      }
    },
    {
      key: 'stamped',
      detect: function () {
        return !!window.StampedFn ||
          has('script[src*="stamped.io"], .stamped-product-reviews-badge, #stamped-main-widget');
      },
      widget: '#stamped-main-widget, .stamped-reviews-widget',
      badge: '.stamped-product-reviews-badge',
      makeWidget: null,
      makeBadge: null,
      paint: function () {
        var s = window.StampedFn;
        if (s && typeof s.loadBadges === 'function') s.loadBadges();
      }
    }
  ];

  function findVendor() {
    for (var i = 0; i < VENDORS.length; i++) {
      try { if (VENDORS[i].detect()) return VENDORS[i]; } catch (e) {}
    }
    return null;
  }

  /* ---------- shared helpers ----------------------------------------- */

  function host(cls) {
    var el = document.createElement('div');
    el.className = cls;
    return el;
  }

  /* Move rather than clone: the vendor holds a reference to the node it
     rendered into and re-renders on filter/paginate. A clone goes stale. */
  function adopt(parent, node) {
    if (node && node.parentNode !== parent) parent.appendChild(node);
  }

  function isInCard(el) {
    return !!(el.closest && el.closest(CARD_SEL));
  }

  /* Vendor nodes that belong to the PDP, not to a related-products card. */
  function pdpNode(sel) {
    var all = document.querySelectorAll(sel);
    for (var i = 0; i < all.length; i++) if (!isInCard(all[i])) return all[i];
    return null;
  }

  /* ---------- PDP ------------------------------------------------------ */

  function productId() {
    try {
      if (window.meta && window.meta.product && window.meta.product.id) {
        return String(window.meta.product.id);
      }
    } catch (e) {}
    try {
      /* Shopify's own page-context global, present on every storefront page. */
      if (window.__st && window.__st.rtyp === 'product' && window.__st.rid) {
        return String(window.__st.rid);
      }
    } catch (e) {}
    return null;
  }

  /* The description block. cc-product.js tags it .cc-desc; the fallback covers
     the case where the catalogue copy loses the "Style tip / Features" shape
     that cc-product.js matches on, so .cc-desc never gets applied. */
  function descBlock() {
    var d = document.querySelector('.cc-desc');
    if (d) return d;
    var col = document.querySelector('.product-details');
    if (!col) return null;
    var rte = col.querySelectorAll('rte-formatter, .rte');
    return rte.length ? rte[rte.length - 1] : null;
  }

  /* Horizon nests the visible title: .product-details holds a hidden
     .view-product-title (a view-transition stub, display:none) and the real
     <h1> lives two group-blocks deep, immediately before <product-price>.
     Anchoring on <product-price> is what puts the stars under the title
     instead of above it. */
  function badgeSlot() {
    var col = document.querySelector('.product-details');
    if (!col) return null;
    var price = col.querySelector('product-price');
    if (price && price.parentNode) return { parent: price.parentNode, before: price };
    var h1 = col.querySelector('h1');
    if (h1 && h1.parentNode) return { parent: h1.parentNode, before: h1.nextSibling };
    return null;
  }

  function mountPdp(v) {
    if (!/\/products\//.test(location.pathname)) return;
    var id = productId();
    if (!id) return;

    /* --- full widget, after the description --- */
    var anchor = descBlock();
    if (anchor) {
      var wrap = document.querySelector('.cc-rvw-pdp');
      var node = pdpNode(v.widget);
      if (!node && !wrap && v.makeWidget) node = v.makeWidget(id);
      if (node) {
        if (!wrap) {
          wrap = host('cc-rvw-pdp');
          anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
        }
        adopt(wrap, node);
      }
    }

    /* --- star badge, between the title and the price --- */
    var slot = badgeSlot();
    if (slot) {
      var bwrap = document.querySelector('.cc-rvw-badge');
      var badge = pdpNode(v.badge);
      if (!badge && !bwrap && v.makeBadge) badge = v.makeBadge(id, 'product');
      if (badge) {
        if (!bwrap) {
          bwrap = host('cc-rvw-badge');
          slot.parent.insertBefore(bwrap, slot.before);
        }
        adopt(bwrap, badge);
      }
    }
  }

  /* ---------- product cards -------------------------------------------- */

  var ID_CACHE = {};

  function cardContent(card) {
    /* .cc-lcard roots carry .product-card__content themselves; Horizon's
       <product-card> wraps one. */
    if (card.matches && card.matches('.product-card__content')) return card;
    return card.querySelector('.product-card__content') || card;
  }

  function cardHost(content) {
    var existing = content.querySelector(':scope > .cc-rvw-card');
    if (existing) return existing;
    var h = host('cc-rvw-card');
    var price = content.querySelector('product-price, .cc-lcard__price, .price');
    if (price && price.parentNode === content) content.insertBefore(h, price);
    else content.appendChild(h);
    return h;
  }

  function whenVisible(el, fn) {
    if (!window.IntersectionObserver) { fn(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); fn(); return; }
      }
    }, { rootMargin: '300px' });
    io.observe(el);
  }

  /* Horizon's cards expose data-product-id. The ChicChac .cc-lcard rail only
     renders a handle, so the numeric id has to come from the product JSON —
     deferred until the card is near the viewport so the homepage's 24 cards
     don't fire 24 requests up front. Adding data-product-id="{{ product.id }}"
     to cc-loved.liquid would remove this path entirely. */
  function resolveId(card, cb) {
    var id = card.getAttribute('data-product-id');
    if (id) { cb(id); return; }
    var h = card.getAttribute('data-product-handle');
    if (!h) {
      var a = card.querySelector('a[href*="/products/"]');
      var m = a && (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      h = m && m[1];
    }
    if (!h) return;
    if (ID_CACHE[h]) { cb(ID_CACHE[h]); return; }
    whenVisible(card, function () {
      if (ID_CACHE[h]) { cb(ID_CACHE[h]); return; }
      fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (p) {
          if (!p || !p.id) return;
          ID_CACHE[h] = String(p.id);
          cb(ID_CACHE[h]);
        })
        .catch(function () {});
    });
  }

  function enhanceCard(v, card) {
    if (card.__ccRvw) return;

    /* If the vendor's installer already put a badge in this card, we only need
       to move it to the design's slot — that path works for all three apps. */
    var existing = card.querySelector(v.badge);
    if (existing) {
      card.__ccRvw = true;
      adopt(cardHost(cardContent(card)), existing);
      return;
    }
    /* No badge and nothing we can build (Loox/Stamped): leave the card
       unflagged so the observer picks it up if one is injected later. */
    if (!v.makeBadge) return;

    card.__ccRvw = true;
    resolveId(card, function (id) {
      adopt(cardHost(cardContent(card)), v.makeBadge(id, 'index'));
      repaint(v);
    });
  }

  function scanCards(v, root) {
    var cards = (root || document).querySelectorAll(CARD_SEL);
    cards.forEach(function (c) { enhanceCard(v, c); });
    return cards.length;
  }

  /* ---------- run ------------------------------------------------------- */

  var painting = false;
  function repaint(v) {
    if (painting) return;
    painting = true;
    setTimeout(function () {
      painting = false;
      try { v.paint(); } catch (e) {}
    }, 60);
  }

  function start(v) {
    mountPdp(v);
    scanCards(v);
    repaint(v);

    /* Collection filters, search results and the vendor's own late injection
       all add nodes after load. mountPdp/enhanceCard are idempotent — they
       no-op once the node is already in its slot — so the moves they make
       cannot feed themselves back through this observer. */
    var queued = false;
    new MutationObserver(function (muts) {
      var touched = false;
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches(CARD_SEL)) { enhanceCard(v, n); touched = true; }
          else if (n.querySelectorAll && scanCards(v, n)) touched = true;
        });
      });
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        mountPdp(v);
        if (touched) repaint(v);
      }, 80);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  var tries = 0;
  function poll() {
    var v = findVendor();
    if (v) { start(v); return; }
    if (++tries >= WAIT_TRIES) return; /* no reviews app on this store — stop, stay inert */
    setTimeout(poll, WAIT_TICK);
  }

  if (document.readyState !== 'loading') poll();
  else document.addEventListener('DOMContentLoaded', poll);
})();
